import { type FormEvent, useEffect, useState } from "react";
import { ApiError, type Catalogue, type OnboardingState, type Profile, api } from "../lib/api";
import { Notice } from "../components/Shell";

export function ProfileStep({ state, onSaved }: { state: OnboardingState; onSaved: (s: OnboardingState) => void }) {
  const p = state.profile;
  const [form, setForm] = useState<Profile>({
    fullName: p?.fullName ?? "",
    email: p?.email ?? "",
    clientType: p?.clientType ?? "INDIVIDUAL",
    accreditedInvestor: p?.accreditedInvestor ?? false,
    dateOfBirth: p?.dateOfBirth ?? "",
    segregationDeclared: p?.segregationDeclared ?? false,
    city: p?.city ?? "",
    state: p?.state ?? "",
  });
  const [states, setStates] = useState<string[]>([]);
  useEffect(() => {
    api<Catalogue>("/v1/catalogue").then((c) => setStates(c.states), () => setStates([]));
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof Profile>(k: K, v: Profile[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(await api<OnboardingState>("/v1/onboarding/profile", { body: form }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save your details.");
    } finally {
      setBusy(false);
    }
  }

  const individual = form.clientType === "INDIVIDUAL";
  return (
    <form onSubmit={save}>
      <h1>Your details</h1>
      <p className="muted">Use your name exactly as it appears on your PAN card.</p>
      {error && <Notice tone="bad">{error}</Notice>}
      <label className="field">
        <span>Full name</span>
        <input type="text" autoComplete="name" value={form.fullName} onChange={(e) => set("fullName", e.target.value)} required />
      </label>
      <label className="field">
        <span>Email</span>
        <input type="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
      </label>
      <div className="row">
        <label className="field">
          <span>You are subscribing as</span>
          <select value={form.clientType} onChange={(e) => set("clientType", e.target.value as Profile["clientType"])}>
            <option value="INDIVIDUAL">An individual</option>
            <option value="HUF">A HUF</option>
            <option value="NON_INDIVIDUAL">A company, firm or trust</option>
          </select>
        </label>
        <label className="field">
          <span>{individual ? "Date of birth" : "Date of incorporation"}</span>
          <input type="date" value={form.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} required />
        </label>
      </div>
      <div className="row">
        <label className="field">
          <span>City</span>
          <input type="text" autoComplete="address-level2" value={form.city} onChange={(e) => set("city", e.target.value)} required />
        </label>
        <label className="field">
          <span>State</span>
          <select value={form.state} onChange={(e) => set("state", e.target.value)} required>
            <option value="" disabled>
              Choose
            </option>
            {states.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={form.accreditedInvestor} onChange={(e) => set("accreditedInvestor", e.target.checked)} />
        <span>I am an accredited investor with a valid accreditation certificate.</span>
      </label>
      <label className="check">
        <input type="checkbox" checked={form.segregationDeclared} onChange={(e) => set("segregationDeclared", e.target.checked)} />
        <span>I do not take distribution services (such as mutual fund or product sales) from Finversify or its group companies.</span>
      </label>
      <button className="btn" disabled={busy}>
        {busy ? "Saving…" : "Save and continue"}
      </button>
    </form>
  );
}
